import type { MantineSize, MantineThemeOverride } from "@mantine/core";

/**
 * The product's one typeface.
 *
 * Screens previously declared their own font on their root class and the
 * investor app ended up split between Inter and Poppins — the Offer Room and
 * Withdrawal in Inter, the Investment Portfolio and Transaction History in
 * Poppins. At identical size and weight the two look meaningfully different
 * (Poppins is geometric with a taller x-height and wider letterforms), so the
 * same `1.5rem/700` page title read as two different sizes depending on which
 * page you were on. Inter is the standard.
 *
 * The stack matters as much as the first entry: `fontFamily: "Inter"` with no
 * fallback drops to the browser's default serif if the webfont fails, which is
 * a far bigger visual break than any of the substitutes below.
 */
export const FONT_FAMILY =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif';

/**
 * The product type scale, roughly one step below Mantine's defaults
 * (xs 12 / sm 14 / md 16 / lg 18 / xl 20) so dense ops tables and forms fit
 * more on screen.
 *
 * `fontSizes` is not just the default text size: every component that reads
 * `size="xs|sm|md|lg|xl"` resolves against it, so inputs, buttons and table
 * cells shrink with it. Two consequences are worth knowing before changing a
 * value here, because neither is visible at the call site:
 *   - Mantine's table text and the default input control both read `sm`.
 *   - Input error, description and success text are derived as `sm - 2px`
 *     (see Input.css), which is why `INPUT_MESSAGE_FONT_SIZE` below pins a
 *     floor rather than letting validation text fall out of this scale.
 */
export const FONT_SIZES = {
  // RAISED from 11px (both apps declared `xs: "0.6875rem"` before this module
  // existed), which moves every `size="xs"` consumer up a pixel — 38 call sites
  // across 21 files in production code, plus Mantine's NavLink description,
  // which reads this step directly. Deliberate, and the one step in the scale
  // that did not come down: `xs` carries the shared interest disclaimer and the
  // withdrawal warnings, disclosure text that is read rather than glanced at.
  // One step below 12px is also where the largest relative jump in the scale
  // would be, which is usually a sign the bottom step has overshot. Floored by
  // constant/__tests__/typography.test.tsx.
  xs: "0.75rem", // 12px
  sm: "0.8125rem", // 13px
  md: "0.875rem", // 14px
  lg: "1rem", // 16px
  xl: "1.125rem", // 18px
  // `MantineSize` is the closed union 'xs' | 'sm' | 'md' | 'lg' | 'xl', so this
  // is what actually rejects a step Mantine has no token for. `as const` keeps
  // the literal types through it. See the note on `typographyTheme` for why the
  // theme-level `satisfies` does not do this job.
} as const satisfies Record<MantineSize, string>;

/**
 * Mirrors the same reduction on `<Title>` (Mantine defaults are
 * 34/26/22/18/16/14px) so headings stay proportional to body text.
 *
 * Only `fontSize` is given per level. `mergeMantineTheme` deep-merges, so each
 * level keeps its default `lineHeight` instead of losing it.
 */
export const HEADING_SIZES = {
  h1: { fontSize: "1.75rem" }, // 28px
  h2: { fontSize: "1.375rem" }, // 22px
  h3: { fontSize: "1.125rem" }, // 18px
  h4: { fontSize: "1rem" }, // 16px
  h5: { fontSize: "0.875rem" }, // 14px
  h6: { fontSize: "0.75rem" }, // 12px
} as const;

/**
 * Floor for input error, description and success text.
 *
 * Mantine does not take these from `fontSizes` — it derives all three as
 * `sm - 2px` (Input.css), so they fall out of the scale rather than being
 * chosen. At sm 13px that puts validation messages at 11px: error red on
 * white, carrying bank details and KYC field errors, read under exactly the
 * conditions that make small text hardest. Pinning them decouples the one
 * thing in the scale that should not shrink from the one that should.
 *
 * It has to be a `vars` override rather than a `:root` declaration. Mantine
 * writes `--input-error-size` as an inline style on the message element for
 * every input, and an element's own declaration beats anything inherited from
 * an ancestor. The size always reaches the resolver, so the "no explicit size"
 * branch never runs: it comes from `InputBase`'s defaultProps, which is what
 * TextInput, Select, NumberInput and the rest are built on. (Not `Input`'s own
 * defaultProps — those apply only to a bare `<Input>`.)
 */
export const INPUT_MESSAGE_FONT_SIZE = "0.75rem"; // 12px

/**
 * Sidebar nav link type size, shared by the admin and investor navbars.
 *
 * Deliberately not taken from `fontSizes`: the sidebar is chrome, so it holds
 * its size while the content scale moves. In rem rather than px all the same —
 * holding against the content scale is a different thing from ignoring the
 * reader's own root font size, and rem gives both. Note where this has to be
 * applied —
 * Mantine's NavLink declares `font-size: var(--mantine-font-size-sm)` on the
 * LABEL element rather than the root, so a size on the NavLink root is
 * overridden rather than inherited. See the `& .mantine-NavLink-label` blocks
 * in the two navbar style files, and the placement guard in
 * e2e/admin/navbar-label-size.spec.ts.
 */
export const NAV_LINK_FONT_SIZE = "0.875rem"; // 14px at the default root

/**
 * The single theme every entry point passes to its MantineProvider.
 *
 * This exists because the scale was previously copy-pasted into the admin and
 * investor `index.tsx` files with a comment asking the next person to keep them
 * in sync. Nothing could enforce that — lint, format and type-check all pass
 * with the two apps on different scales — and the test harness carried no theme
 * at all, so no test could detect the drift either. One exported object removes
 * the possibility rather than documenting it. The apps are pinned to it by
 * __tests__/typography.entryPoints.test.ts and the harness by
 * test-utils/__tests__/renderWithProviders.theme.test.tsx.
 *
 * `satisfies MantineThemeOverride` checks the SHAPE and nothing more. It does
 * NOT reject an unknown size token, which is worth knowing because an earlier
 * version of this comment claimed it did: `MantineFontSize` resolves to
 * `... | (string & {})`, an open index signature Mantine provides so consumers
 * can extend the scale, so `{ fontSizes: { xxl: "2rem" } }` type-checks clean.
 * What closes it is the `satisfies Record<MantineSize, string>` on FONT_SIZES
 * above.
 *
 * Neither catches a READ of a token that does not exist —
 * `theme.fontSizes.xxl` against the resolved `MantineTheme` stays legal by
 * design, and resolves to `undefined` at runtime, which makes emotion drop the
 * declaration silently. Only a rendered-size assertion catches that; the one
 * that caught the real instance is
 * admin-fundedhere/src/screens/SalesOnboarding/__tests__/Onboarding.style.test.tsx.
 */
export const typographyTheme = {
  fontFamily: FONT_FAMILY,
  fontSizes: FONT_SIZES,
  headings: { fontFamily: FONT_FAMILY, sizes: HEADING_SIZES },
  components: {
    // Theme-level `vars` merge with the component's own resolver rather than
    // replacing it, so naming only these three parts leaves --input-label-size,
    // --input-height and --input-fz untouched.
    InputWrapper: {
      vars: () => ({
        error: { "--input-error-size": INPUT_MESSAGE_FONT_SIZE },
        success: { "--input-success-size": INPUT_MESSAGE_FONT_SIZE },
        description: { "--input-description-size": INPUT_MESSAGE_FONT_SIZE },
      }),
    },
  },
} satisfies MantineThemeOverride;

export default FONT_FAMILY;
